import React, { useState, useEffect } from 'react';
import { 
  FolderIcon, 
  FolderPlusIcon, 
  DocumentTextIcon, 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  UserIcon,
  XMarkIcon

} from '@heroicons/react/24/outline';
import type { PastaProcedimento, ProcedimentoSimples, Colaborador } from '../../types';

interface ProcedimentosSimplesProps {
  session?: { 
    usuario: { 
      role?: string; 
      empresaId?: string;
      id?: string;
      nome?: string;
    } 
  };
  departamentoIdFiltro?: string | null;
}

const ProcedimentosSimples: React.FC<ProcedimentosSimplesProps> = ({ session, departamentoIdFiltro }) => {
  const [pastas, setPastas] = useState<PastaProcedimento[]>([]);
  const [procedimentos, setProcedimentos] = useState<ProcedimentoSimples[]>([]);
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [pastaExpandida, setPastaExpandida] = useState<string | null>(null);
  const [mostrarModalPasta, setMostrarModalPasta] = useState(false);
  const [mostrarModalProcedimento, setMostrarModalProcedimento] = useState(false);
  const [mostrarModalEditarPasta, setMostrarModalEditarPasta] = useState(false);
  const [mostrarModalEditarProcedimento, setMostrarModalEditarProcedimento] = useState(false);
  const [procedimentoEditando, setProcedimentoEditando] = useState<ProcedimentoSimples | null>(null);
  const [pastaEditando, setPastaEditando] = useState<PastaProcedimento | null>(null);
  const [pastaSelecionada, setPastaSelecionada] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [salvando, setSalvando] = useState(false);
  
  // Estado para procedimento visualizado (acessado via link direto)
  const [procedimentoVisualizado, setProcedimentoVisualizado] = useState<ProcedimentoSimples | null>(null);
  const [mostrarModalVisualizacao, setMostrarModalVisualizacao] = useState(false);
  




  
  // Estados para novos itens
  const [novaPasta, setNovaPasta] = useState({
    nome: '',
    descricao: '',
    cor: '#3B82F6'
  });
  
  const [novoProcedimento, setNovoProcedimento] = useState({
    nome: '',
    objetivo: '',
    arquivo: null as File | null,
    colaboradores: [] as string[]
  });

  const cores = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
    '#8B5CF6', '#F97316', '#06B6D4', '#EC4899'
  ];

  // Carregar dados ao montar o componente
  useEffect(() => {
    carregarDados();
  }, []);

  // Verificar se há procedimento pendente para abrir (link direto do email)
  useEffect(() => {
    const pendingId = sessionStorage.getItem('pendingProcedimentoId');
    if (pendingId && procedimentos.length > 0 && !loading) {
      console.log('📋 Buscando procedimento pendente:', pendingId);
      
      // Buscar o procedimento pelo ID
      const procEncontrado = procedimentos.find(p => 
        (p.id === pendingId) || 
        ((p as any)._id === pendingId) ||
        (p.id?.toString() === pendingId)
      );
      
      if (procEncontrado) {
        console.log('✅ Procedimento encontrado:', procEncontrado.nome);
        
        // Expandir a pasta do procedimento
        if (procEncontrado.pastaId) {
          setPastaExpandida(procEncontrado.pastaId);
        }
        
        // Abrir modal de visualização
        setProcedimentoVisualizado(procEncontrado);
        setMostrarModalVisualizacao(true);
      } else {
        console.warn('⚠️ Procedimento não encontrado:', pendingId);
      }
      
      // Limpar do sessionStorage
      sessionStorage.removeItem('pendingProcedimentoId');
    }
  }, [procedimentos, loading]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('🔄 Iniciando carregamento de dados...');

      // Carregar pastas
      console.log('📁 Buscando pastas...');
      const userEmpresaId = session?.usuario?.empresaId;
      const urlPastas = userEmpresaId ? `/api/pastas-procedimentos?empresaId=${userEmpresaId}` : '/api/pastas-procedimentos';
      console.log('📁 URL de busca:', urlPastas, 'empresaId:', userEmpresaId);
      const responsePastas = await fetch(urlPastas);
      console.log('📁 Response pastas:', responsePastas.status, responsePastas.statusText);
      
      if (!responsePastas.ok) {
        throw new Error(`Erro ao carregar pastas: ${responsePastas.status}`);
      }
      const dataPastas = await responsePastas.json();
      console.log('📁 Dados das pastas:', dataPastas);

      // Carregar procedimentos
      console.log('📄 Buscando procedimentos...');
      const responseProcedimentos = await fetch('/api/procedimentos-simples');
      console.log('📄 Response procedimentos:', responseProcedimentos.status, responseProcedimentos.statusText);
      
      if (!responseProcedimentos.ok) {
        throw new Error(`Erro ao carregar procedimentos: ${responseProcedimentos.status}`);
      }
      const dataProcedimentos = await responseProcedimentos.json();
      console.log('📄 Dados dos procedimentos:', dataProcedimentos);

      // Carregar colaboradores
      console.log('👥 Buscando colaboradores...');
      const empresaId = session?.usuario?.empresaId || '';
      console.log('👥 EmpresaId da sessão:', empresaId);
      
      try {
        const responseColaboradores = await fetch(`/api/colaboradores?empresaId=${encodeURIComponent(empresaId)}`);
        console.log('👥 Response colaboradores:', responseColaboradores.status, responseColaboradores.statusText);
        
        if (responseColaboradores.ok) {
          const dataColaboradores = await responseColaboradores.json();
          console.log('👥 Dados dos colaboradores:', dataColaboradores);
          
          if (dataColaboradores.success && dataColaboradores.data && dataColaboradores.data.length > 0) {
            // Mapear _id para id para compatibilidade
            const colaboradoresMapeados = dataColaboradores.data.map((col: any) => ({
              ...col,
              id: col._id || col.id
            }));
            setColaboradores(colaboradoresMapeados);
            console.log('✅ Colaboradores carregados da API:', colaboradoresMapeados.length);
          } else {
            console.error('❌ API retornou dados vazios');
          }
        } else {
          console.error('❌ API retornou erro:', responseColaboradores.status);
        }
      } catch (error) {
        console.error('❌ Erro ao carregar colaboradores:', error);
      }

      if (dataPastas.success) {
        setPastas(dataPastas.data || []);
        console.log('✅ Pastas carregadas:', dataPastas.data?.length || 0);
      } else {
        throw new Error(dataPastas.error || 'Erro ao carregar pastas');
      }

      if (dataProcedimentos.success) {
        setProcedimentos(dataProcedimentos.data || []);
        console.log('✅ Procedimentos carregados:', dataProcedimentos.data?.length || 0);
      } else {
        throw new Error(dataProcedimentos.error || 'Erro ao carregar procedimentos');
      }

    } catch (err) {
      console.error('❌ Erro ao carregar dados:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  };

  // Função para salvar pasta
  const handleSalvarPasta = async () => {
    if (!novaPasta.nome.trim()) return;

    const pasta: Omit<PastaProcedimento, 'id'> = {
      nome: novaPasta.nome,
      descricao: novaPasta.descricao,
      cor: novaPasta.cor,
      createdAt: new Date(),
      updatedAt: new Date(),
      empresaId: session?.usuario?.empresaId || ''
    };

    console.log('💾 Salvando pasta:', pasta);

    try {
      const response = await fetch('/api/pastas-procedimentos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pasta)
      });

      console.log('📡 Response salvar pasta:', response.status, response.statusText);

      const data = await response.json();
      console.log('📊 Dados retornados:', data);
      
      if (data.success) {
        setPastas([...pastas, data.data]);
        setNovaPasta({ nome: '', descricao: '', cor: '#3B82F6' });
        setMostrarModalPasta(false);
        console.log('✅ Pasta salva com sucesso!');
      } else {
        console.error('❌ Erro do servidor:', data.error);
        setError(data.error || 'Erro ao salvar pasta');
      }
    } catch (error) {
      console.error('❌ Erro de conexão:', error);
      setError('Erro de conexão ao salvar pasta');
    }
  };

  // Função para salvar procedimento
  const handleSalvarProcedimento = async () => {
    if (!novoProcedimento.nome.trim() || !novoProcedimento.objetivo.trim() || !pastaSelecionada) return;

    setSalvando(true);
    try {
    let arquivoData = null;
    if (novoProcedimento.arquivo) {
      // Upload local via FormData (sem base64!)
      try {
        const pastaEncontrada = pastas.find(p => p.id === pastaSelecionada);
        const pastaNome = pastaEncontrada?.nome || 'Geral';
        console.log('📤 UPLOAD DEBUG - pastaSelecionada:', pastaSelecionada, 'pastaEncontrada:', pastaEncontrada, 'pastaNome:', pastaNome, 'procNome:', novoProcedimento.nome);
        const formData = new FormData();
        formData.append('file', novoProcedimento.arquivo);
        formData.append('empresaId', pastaNome);
        formData.append('departamentoId', novoProcedimento.nome);

        const uploadRes = await fetch('/api/procedimentos-simples/upload', {
          method: 'POST',
          body: formData
        });
        const uploadData = await uploadRes.json();
        console.log('📤 UPLOAD RESULT:', uploadData);
        if (uploadData.success) {
          arquivoData = {
            nome: uploadData.originalName || novoProcedimento.arquivo.name,
            tipo: uploadData.type || novoProcedimento.arquivo.type,
            tamanho: uploadData.size || novoProcedimento.arquivo.size,
            url: uploadData.url,
            uploadedAt: new Date()
          };
        } else {
          setError(uploadData.error || 'Erro ao fazer upload do arquivo');
          return;
        }
      } catch (uploadErr) {
        console.error('Erro no upload:', uploadErr);
        setError('Erro ao fazer upload do arquivo');
        return;
      }
    }

    const procedimento: Omit<ProcedimentoSimples, 'id'> = {
      nome: novoProcedimento.nome,
      objetivo: novoProcedimento.objetivo,
      arquivo: arquivoData,
      pastaId: pastaSelecionada,
      colaboradores: novoProcedimento.colaboradores,
      createdAt: new Date(),
      updatedAt: new Date(),
      empresaId: session?.usuario?.empresaId || '',
      criadoPor: session?.usuario?.id || ''
    };

    try {
      const response = await fetch('/api/procedimentos-simples', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(procedimento)
      });

      const data = await response.json();
      
      if (data.success) {
        setProcedimentos([...procedimentos, data.data]);
        setNovoProcedimento({ nome: '', objetivo: '', arquivo: null, colaboradores: [] });
        setMostrarModalProcedimento(false);
        setPastaSelecionada('');
      } else {
        console.error('Erro ao salvar procedimento:', data.error);
        setError(data.error || 'Erro ao salvar procedimento');
      }
    } catch (error) {
      console.error('Erro ao salvar procedimento:', error);
      setError('Erro de conexão ao salvar procedimento');
    }
    } finally {
      setSalvando(false);
    }
  };

  // Abrir modal de edição de procedimento
  const handleEditarProcedimento = (proc: ProcedimentoSimples) => {
    setProcedimentoEditando(proc);
    setNovoProcedimento({ nome: proc.nome, objetivo: proc.objetivo, arquivo: null, colaboradores: proc.colaboradores || [] });
    setPastaSelecionada(proc.pastaId);
    setMostrarModalEditarProcedimento(true);
  };

  // Salvar edição de procedimento
  const handleSalvarEdicaoProcedimento = async () => {
    if (!procedimentoEditando) return;
    if (!novoProcedimento.nome.trim() || !novoProcedimento.objetivo.trim() || !pastaSelecionada) return;

    setSalvando(true);
    try {
    let arquivoData = procedimentoEditando.arquivo || null;
    if (novoProcedimento.arquivo) {
      // Upload local via FormData (sem base64!)
      try {
        const pastaNome = pastas.find(p => p.id === pastaSelecionada)?.nome || 'Geral';
        const formData = new FormData();
        formData.append('file', novoProcedimento.arquivo);
        formData.append('empresaId', pastaNome);
        formData.append('departamentoId', novoProcedimento.nome);

        const uploadRes = await fetch('/api/procedimentos-simples/upload', {
          method: 'POST',
          body: formData
        });
        const uploadData = await uploadRes.json();
        if (uploadData.success) {
          arquivoData = {
            nome: uploadData.originalName || novoProcedimento.arquivo.name,
            tipo: uploadData.type || novoProcedimento.arquivo.type,
            tamanho: uploadData.size || novoProcedimento.arquivo.size,
            url: uploadData.url,
            uploadedAt: new Date()
          };
        } else {
          setError(uploadData.error || 'Erro ao fazer upload do arquivo');
          return;
        }
      } catch (uploadErr) {
        console.error('Erro no upload:', uploadErr);
        setError('Erro ao fazer upload do arquivo');
        return;
      }
    }

    const payload = {
      nome: novoProcedimento.nome,
      objetivo: novoProcedimento.objetivo,
      arquivo: arquivoData,
      pastaId: pastaSelecionada,
      colaboradores: novoProcedimento.colaboradores,
      updatedAt: new Date()
    };

    try {
      const response = await fetch(`/api/procedimentos-simples/${procedimentoEditando.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        const atualizados = procedimentos.map(p => p.id === procedimentoEditando.id ? { ...p, ...data.data } : p);
        setProcedimentos(atualizados);
        setMostrarModalEditarProcedimento(false);
        setProcedimentoEditando(null);
        setNovoProcedimento({ nome: '', objetivo: '', arquivo: null, colaboradores: [] });
        setPastaSelecionada('');
      } else {
        setError(data.error || 'Erro ao editar procedimento');
      }
    } catch (e) {
      console.error('Erro ao editar procedimento:', e);
      setError('Erro de conexão ao editar procedimento');
    }
    } finally {
      setSalvando(false);
    }
  };

  // Função para baixar arquivo
  const handleBaixarArquivo = (procedimento: ProcedimentoSimples) => {
    if (!procedimento.arquivo) return;

    const link = document.createElement('a');
    // Compatibilidade: priorizar URL local, fallback para dataUrl (legado base64)
    const url = (procedimento.arquivo as any).url || (procedimento.arquivo as any).dataUrl;
    if (!url) return;
    link.href = url;
    link.download = procedimento.arquivo.nome;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Função para visualizar arquivo
  const handleVisualizarArquivo = (procedimento: ProcedimentoSimples) => {
    if (!procedimento.arquivo) return;

    // Compatibilidade: priorizar URL local, fallback para dataUrl (legado base64)
    const url = (procedimento.arquivo as any).url || (procedimento.arquivo as any).dataUrl;
    if (!url) return;

    // Se é URL do servidor, abrir direto
    if (url.startsWith('/api/')) {
      window.open(url, '_blank');
      return;
    }

    // Fallback para dataUrl (legado base64)
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <title>${procedimento.arquivo.nome}</title>
            <style>
              body { margin: 0; padding: 0; }
              embed, iframe { width: 100%; height: 100vh; }
            </style>
          </head>
          <body>
            <embed src="${url}" type="${procedimento.arquivo.tipo}" width="100%" height="100%" />
          </body>
        </html>
      `);
    }
  };

  // Função para editar pasta
  const handleEditarPasta = (pasta: PastaProcedimento) => {
    setPastaEditando(pasta);
    setNovaPasta({
      nome: pasta.nome,
      descricao: pasta.descricao || '',
      cor: pasta.cor || '#3B82F6'
    });
    setMostrarModalEditarPasta(true);
  };

  // Função para salvar edição da pasta
  const handleSalvarEdicaoPasta = async () => {
    if (!pastaEditando || !novaPasta.nome.trim()) return;

    try {
      const response = await fetch(`/api/pastas-procedimentos/${pastaEditando.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nome: novaPasta.nome,
          descricao: novaPasta.descricao,
          cor: novaPasta.cor,
          updatedAt: new Date()
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Atualizar a lista de pastas
        const pastasAtualizadas = pastas.map(p => 
          p.id === pastaEditando.id 
            ? { ...p, ...data.data }
            : p
        );
        setPastas(pastasAtualizadas);
        setMostrarModalEditarPasta(false);
        setPastaEditando(null);
        setNovaPasta({ nome: '', descricao: '', cor: '#3B82F6' });
      } else {
        console.error('Erro ao editar pasta:', data.error);
        setError(data.error || 'Erro ao editar pasta');
      }
    } catch (error) {
      console.error('Erro ao editar pasta:', error);
      setError('Erro de conexão ao editar pasta');
    }
  };

  // Função para excluir pasta
  const handleExcluirPasta = async (pasta: PastaProcedimento) => {
    // Verificar se há procedimentos na pasta
    const procedimentosNaPasta = procedimentos.filter(proc => proc.pastaId === pasta.id);
    if (procedimentosNaPasta.length > 0) {
      alert(`❌ Não é possível excluir a pasta "${pasta.nome}"!\n\nEla contém ${procedimentosNaPasta.length} procedimento(s).\n\nExclua os procedimentos primeiro.`);
      return;
    }

    if (!window.confirm(`🗑️ Tem certeza que deseja excluir a pasta "${pasta.nome}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/pastas-procedimentos/${pasta.id}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        // Remover a pasta da lista
        setPastas(pastas.filter(p => p.id !== pasta.id));
        console.log('✅ Pasta excluída com sucesso!');
        alert(`✅ Pasta "${pasta.nome}" excluída com sucesso!`);
      } else {
        console.error('❌ Erro do servidor:', data.error);
        alert(`❌ Erro: ${data.error}`);
        // Recarregar listas para garantir sincronização
        await carregarDados();
      }
    } catch (error) {
      console.error('❌ Erro de conexão:', error);
      alert('❌ Erro de conexão ao excluir pasta');
    }
  };

  const getProcedimentosPorPasta = (pastaId: string) => {
    let filtered = procedimentos.filter(proc => proc.pastaId === pastaId);
    
    // Se há um filtro de departamento ativo, filtrar também por departamento
    if (departamentoIdFiltro) {
      filtered = filtered.filter(proc => {
        const procDeptId = (proc as any).departamentoId || (proc as any).departamento;
        return procDeptId === departamentoIdFiltro;
      });
    }
    
    console.log(`📊 getProcedimentosPorPasta("${pastaId}"):`, filtered.length, 'procedimentos');
    if (departamentoIdFiltro) {
      console.log(`📊 Filtrado por departamento:`, departamentoIdFiltro);
    }
    console.log(`📊 Procedimentos totais:`, procedimentos.length);
    console.log(`📊 Procedimentos da pasta:`, filtered.map(p => ({ nome: p.nome, pastaId: p.pastaId })));
    return filtered;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 kanban-desktop-scale">
      {/* Background decoration to match FlowList */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando procedimentos...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erro</h3>
                <div className="text-sm text-red-700 mt-1">{error}</div>
                <div className="mt-3">
                  <button
                    onClick={() => {
                      setError('');
                      carregarDados();
                    }}
                    className="text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded-md transition-colors"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Conteúdo principal */}
        {!loading && !error && (
        <section className="relative rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-sm shadow-lg">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-600 to-purple-600"></div>
          <div className="p-6">
            {/* Banner de filtro de departamento */}
            {departamentoIdFiltro && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FolderIcon className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-blue-900">
                    Filtrando procedimentos do departamento selecionado
                  </span>
                </div>
                <button
                  onClick={() => window.location.href = '#'}
                  className="text-xs text-blue-700 hover:text-blue-900 underline"
                >
                  Limpar filtro
                </button>
              </div>
            )}
            
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Procedimentos</h1>
                <p className="text-gray-600 mt-2">Organize seus procedimentos em pastas e mantenha tudo documentado</p>
              </div>
              <div className="flex flex-wrap gap-3 w-full md:w-auto">
                <button
                  onClick={() => setMostrarModalPasta(true)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition-colors w-full md:w-auto"
                >
                  <FolderPlusIcon className="h-5 w-5" />
                  Nova Pasta
                </button>
                <button
                  onClick={() => setMostrarModalProcedimento(true)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-sm hover:from-indigo-700 hover:to-purple-700 hover:shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
                  disabled={pastas.length === 0}
                >
                  <PlusIcon className="h-5 w-5" />
                  Novo Procedimento
                </button>
              </div>
            </div>

            {/* Lista de Pastas */}
            {pastas.length === 0 ? (
              <div className="text-center py-16 bg-white/70 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100">
                <FolderIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma pasta criada</h3>
                <p className="text-gray-500 mb-6">Comece criando uma pasta para organizar seus procedimentos</p>
                <button
                  onClick={() => setMostrarModalPasta(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-sm hover:from-indigo-700 hover:to-purple-700 hover:shadow transition-colors"
                >
                  <FolderPlusIcon className="h-5 w-5" />
                  Criar Primeira Pasta
                </button>
              </div>
            ) : (
              // Grid responsivo: 1 coluna no mobile e 2 colunas em telas md+
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pastas.map((pasta) => {
            const procedimentosDaPasta = getProcedimentosPorPasta(pasta.id);
            const isExpanded = pastaExpandida === pasta.id;
            
            return (
              <div key={pasta.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Cabeçalho da Pasta */}
                <div 
                  className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setPastaExpandida(isExpanded ? null : pasta.id)}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="p-3 rounded-lg text-white"
                      style={{ backgroundColor: pasta.cor }}
                    >
                      <FolderIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{pasta.nome}</h3>
                      {pasta.descricao && (
                        <p className="text-gray-600 text-sm">{pasta.descricao}</p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">
                        {procedimentosDaPasta.length} procedimento(s)
                      </p>
                    </div>
                  </div>
                  
          <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPastaSelecionada(pasta.id);
                        setMostrarModalProcedimento(true);
                      }}
            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title="Adicionar procedimento"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditarPasta(pasta);
                      }}
            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title="Editar pasta"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExcluirPasta(pasta);
                      }}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Excluir pasta"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Lista de Procedimentos */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50">
                    {procedimentosDaPasta.length === 0 ? (
                      <div className="p-6 text-center">
                        <DocumentTextIcon className="mx-auto h-8 w-8 text-indigo-500 mb-2" />
                        <p className="text-gray-500">Nenhum procedimento nesta pasta</p>
                        <button
                          onClick={() => {
                            setPastaSelecionada(pasta.id);
                            setMostrarModalProcedimento(true);
                          }}
                          className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-white text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition-colors text-sm"
                        >
                          <PlusIcon className="h-4 w-4" />
                          Adicionar Procedimento
                        </button>
                      </div>
                    ) : (
                      <div className="p-4 space-y-3">
                        {procedimentosDaPasta.map((procedimento) => (
                          <div 
                            key={procedimento.id}
                            className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-sm transition-shadow"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                <div className="p-2 bg-indigo-50 rounded-lg">
                                  <DocumentTextIcon className="h-5 w-5 text-indigo-600" />
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900">{procedimento.nome}</h4>
                                  <p className="text-gray-600 text-sm mt-1">{procedimento.objetivo}</p>
                                  {procedimento.arquivo && (
                                    <div className="flex items-center gap-2 mt-2">
                                      <span className="text-xs text-gray-500">📎 {procedimento.arquivo.nome}</span>
                                      <span className="text-xs text-gray-400">
                                        ({Math.round(procedimento.arquivo.tamanho / 1024)}KB)
                                      </span>
                                    </div>
                                  )}
                                  {procedimento.colaboradores && procedimento.colaboradores.length > 0 && (
                                    <div className="mt-3">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs text-gray-500 font-medium">Responsáveis:</span>
                                        {procedimento.colaboradores.map((colaboradorId: string) => {
                                          const colaborador = colaboradores.find(c => c.id === colaboradorId);
                                          return colaborador ? (
                                            <span 
                                              key={colaboradorId}
                                              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 border border-blue-200"
                                            >
                                              <span className="w-2 h-2 bg-blue-500 rounded-full mr-1.5"></span>
                                              {colaborador.nome}
                                            </span>
                                          ) : null;
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-1">
                                {procedimento.arquivo && (
                                  <>
                                    <button
                                      onClick={() => handleVisualizarArquivo(procedimento)}
                                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                      title="Visualizar"
                                    >
                                      <EyeIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleBaixarArquivo(procedimento)}
                                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                      title="Baixar"
                                    >
                                      <DocumentArrowDownIcon className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => handleEditarProcedimento(procedimento)}
                                  className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                  title="Editar"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
              </div>
            )}

          </div>
        </section>
      )}

      {/* Modal Nova Pasta */}
      {mostrarModalPasta && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Nova Pasta</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome da Pasta *
                  </label>
                  <input
                    type="text"
                    value={novaPasta.nome}
                    onChange={(e) => setNovaPasta({ ...novaPasta, nome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Processos Administrativos"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição (opcional)
                  </label>
                  <textarea
                    value={novaPasta.descricao}
                    onChange={(e) => setNovaPasta({ ...novaPasta, descricao: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                    placeholder="Descrição da pasta..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cor
                  </label>
                  <div className="flex gap-2">
                    {cores.map((cor) => (
                      <button
                        key={cor}
                        onClick={() => setNovaPasta({ ...novaPasta, cor })}
                        className={`w-8 h-8 rounded-full border-2 ${
                          novaPasta.cor === cor ? 'border-indigo-600' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: cor }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setMostrarModalPasta(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSalvarPasta}
                  disabled={!novaPasta.nome.trim()}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-sm hover:from-indigo-700 hover:to-purple-700 hover:shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Criar Pasta
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Novo Procedimento */}
      {mostrarModalProcedimento && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Novo Procedimento</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {!pastaSelecionada && (
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Selecionar Pasta *
                    </label>
                    <select
                      value={pastaSelecionada}
                      onChange={(e) => setPastaSelecionada(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">Escolha uma pasta...</option>
                      {pastas.map((pasta) => (
                        <option key={pasta.id} value={pasta.id}>{pasta.nome}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome do Procedimento *
                  </label>
                  <input
                    type="text"
                    value={novoProcedimento.nome}
                    onChange={(e) => setNovoProcedimento({ ...novoProcedimento, nome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Como solicitar férias"
                  />
                </div>
                
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Objetivo *
                  </label>
                  <textarea
                    value={novoProcedimento.objetivo}
                    onChange={(e) => setNovoProcedimento({ ...novoProcedimento, objetivo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                    placeholder="Descreva o objetivo deste procedimento..."
                  />
                </div>
                
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Arquivo do Procedimento (PDF ou Word)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (file && file.size > 16 * 1024 * 1024) { // 16MB em bytes
                        alert('⚠️ Arquivo muito grande! O limite é de 16MB por documento.');
                        e.target.value = ''; // Limpar o input
                        return;
                      }
                      setNovoProcedimento({ 
                        ...novoProcedimento, 
                        arquivo: file 
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-600 file:text-white hover:file:bg-indigo-700"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 <strong>Limite:</strong> 16MB por arquivo | <strong>Formatos:</strong> PDF, DOC, DOCX
                  </p>
                  {novoProcedimento.arquivo && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">
                        📎 {novoProcedimento.arquivo.name} 
                        <span className={`ml-2 px-2 py-1 rounded text-xs ${
                          novoProcedimento.arquivo.size > 16 * 1024 * 1024 
                            ? 'bg-red-100 text-red-700' 
                            : novoProcedimento.arquivo.size > 8 * 1024 * 1024
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {(novoProcedimento.arquivo.size / (1024 * 1024)).toFixed(1)}MB
                        </span>
                      </p>
                      {novoProcedimento.arquivo.size > 8 * 1024 * 1024 && (
                        <p className="text-xs text-amber-600 mt-1">
                          ⚠️ Arquivo grande, pode demorar para carregar
                        </p>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Seletor de Colaboradores */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Colaboradores Responsáveis
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto bg-gray-50">
                    {colaboradores.length === 0 ? (
                      <p className="text-gray-500 text-sm">Nenhum colaborador cadastrado</p>
                    ) : (
                      <div className="space-y-2">
                        {colaboradores.map(colaborador => (
                          <label
                            key={colaborador.id}
                            className="flex items-center p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={novoProcedimento.colaboradores.includes(colaborador.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNovoProcedimento({
                                    ...novoProcedimento,
                                    colaboradores: [...novoProcedimento.colaboradores, colaborador.id]
                                  });
                                } else {
                                  setNovoProcedimento({
                                    ...novoProcedimento,
                                    colaboradores: novoProcedimento.colaboradores.filter(id => id !== colaborador.id)
                                  });
                                }
                              }}
                              className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                <UserIcon className="h-4 w-4 text-indigo-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{colaborador.nome}</p>
                                <p className="text-xs text-gray-500">
                                  {colaborador.cargo} {colaborador.isGestor && '• Gestor'}
                                </p>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {novoProcedimento.colaboradores.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Colaboradores Selecionados ({novoProcedimento.colaboradores.length}):
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {novoProcedimento.colaboradores.map(colaboradorId => {
                          const colaborador = colaboradores.find(c => c.id === colaboradorId);
                          return colaborador ? (
                            <span
                              key={colaboradorId}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full"
                            >
                              <UserIcon className="h-3 w-3" />
                              {colaborador.nome}
                              <button
                                type="button"
                                onClick={() => {
                                  setNovoProcedimento({
                                    ...novoProcedimento,
                                    colaboradores: novoProcedimento.colaboradores.filter(id => id !== colaboradorId)
                                  });
                                }}
                                className="ml-1 hover:text-indigo-600"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setMostrarModalProcedimento(false);
                    setPastaSelecionada('');
                    setNovoProcedimento({ nome: '', objetivo: '', arquivo: null, colaboradores: [] });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSalvarProcedimento}
                  disabled={!novoProcedimento.nome.trim() || !novoProcedimento.objetivo.trim() || !pastaSelecionada || salvando}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-sm hover:from-indigo-700 hover:to-purple-700 hover:shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {salvando ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Criando...
                    </>
                  ) : (
                    'Criar Procedimento'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Pasta */}
      {mostrarModalEditarPasta && pastaEditando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Editar Pasta</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome da Pasta *
                  </label>
                  <input
                    type="text"
                    value={novaPasta.nome}
                    onChange={(e) => setNovaPasta({ ...novaPasta, nome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Processos Administrativos"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição (opcional)
                  </label>
                  <textarea
                    value={novaPasta.descricao}
                    onChange={(e) => setNovaPasta({ ...novaPasta, descricao: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                    placeholder="Descrição da pasta..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cor
                  </label>
                  <div className="flex gap-2">
                    {cores.map((cor) => (
                      <button
                        key={cor}
                        onClick={() => setNovaPasta({ ...novaPasta, cor })}
                        className={`w-8 h-8 rounded-full border-2 ${
                          novaPasta.cor === cor ? 'border-indigo-600' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: cor }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setMostrarModalEditarPasta(false);
                    setPastaEditando(null);
                    setNovaPasta({ nome: '', descricao: '', cor: '#3B82F6' });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSalvarEdicaoPasta}
                  disabled={!novaPasta.nome.trim()}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-sm hover:from-indigo-700 hover:to-purple-700 hover:shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Salvar Alterações
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Procedimento */}
      {mostrarModalEditarProcedimento && procedimentoEditando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Editar Procedimento</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pasta</label>
                  <select
                    value={pastaSelecionada}
                    onChange={(e) => setPastaSelecionada(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    {pastas.map((p) => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                  <input
                    type="text"
                    value={novoProcedimento.nome}
                    onChange={(e) => setNovoProcedimento({ ...novoProcedimento, nome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Objetivo</label>
                  <textarea
                    value={novoProcedimento.objetivo}
                    onChange={(e) => setNovoProcedimento({ ...novoProcedimento, objetivo: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Substituir Arquivo (opcional)</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (file && file.size > 16 * 1024 * 1024) { // 16MB em bytes
                        alert('⚠️ Arquivo muito grande! O limite é de 16MB por documento.');
                        e.target.value = '';
                        return;
                      }
                      setNovoProcedimento({ ...novoProcedimento, arquivo: file });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-600 file:text-white hover:file:bg-indigo-700"
                  />
                </div>

                {/* Seletor de Colaboradores */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Colaboradores Responsáveis
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto bg-gray-50">
                    {colaboradores.length === 0 ? (
                      <p className="text-gray-500 text-sm">Nenhum colaborador cadastrado</p>
                    ) : (
                      <div className="space-y-2">
                        {colaboradores.map(colaborador => (
                          <label
                            key={colaborador.id}
                            className="flex items-center p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={novoProcedimento.colaboradores.includes(colaborador.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNovoProcedimento({
                                    ...novoProcedimento,
                                    colaboradores: [...novoProcedimento.colaboradores, colaborador.id]
                                  });
                                } else {
                                  setNovoProcedimento({
                                    ...novoProcedimento,
                                    colaboradores: novoProcedimento.colaboradores.filter(id => id !== colaborador.id)
                                  });
                                }
                              }}
                              className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                <UserIcon className="h-4 w-4 text-indigo-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{colaborador.nome}</p>
                                <p className="text-xs text-gray-500">
                                  {colaborador.cargo} {colaborador.isGestor && '• Gestor'}
                                </p>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {novoProcedimento.colaboradores.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Colaboradores Selecionados ({novoProcedimento.colaboradores.length}):
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {novoProcedimento.colaboradores.map(colaboradorId => {
                          const colaborador = colaboradores.find(c => c.id === colaboradorId);
                          return colaborador ? (
                            <span
                              key={colaboradorId}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full"
                            >
                              <UserIcon className="h-3 w-3" />
                              {colaborador.nome}
                              <button
                                type="button"
                                onClick={() => {
                                  setNovoProcedimento({
                                    ...novoProcedimento,
                                    colaboradores: novoProcedimento.colaboradores.filter(id => id !== colaboradorId)
                                  });
                                }}
                                className="ml-1 hover:text-indigo-600"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setMostrarModalEditarProcedimento(false);
                    setProcedimentoEditando(null);
                    setNovoProcedimento({ nome: '', objetivo: '', arquivo: null, colaboradores: [] });
                    setPastaSelecionada('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSalvarEdicaoProcedimento}
                  disabled={!novoProcedimento.nome.trim() || !novoProcedimento.objetivo.trim() || !pastaSelecionada || salvando}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-sm hover:from-indigo-700 hover:to-purple-700 hover:shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {salvando ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Salvando...
                    </>
                  ) : (
                    'Salvar Alterações'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Visualização de Procedimento (acesso via link) */}
      {mostrarModalVisualizacao && procedimentoVisualizado && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: pastas.find(p => p.id === procedimentoVisualizado.pastaId)?.cor || '#3B82F6' }}
                  >
                    <DocumentTextIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{procedimentoVisualizado.nome}</h2>
                    <p className="text-sm text-gray-500">
                      {pastas.find(p => p.id === procedimentoVisualizado.pastaId)?.nome || 'Sem pasta'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setMostrarModalVisualizacao(false);
                    setProcedimentoVisualizado(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Objetivo */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">📋 Objetivo</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{procedimentoVisualizado.objetivo}</p>
                </div>
              </div>

              {/* Colaboradores */}
              {procedimentoVisualizado.colaboradoresIds && procedimentoVisualizado.colaboradoresIds.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">👥 Colaboradores Responsáveis</h3>
                  <div className="flex flex-wrap gap-2">
                    {procedimentoVisualizado.colaboradoresIds.map(colabId => {
                      const colab = colaboradores.find(c => c.id === colabId);
                      return colab ? (
                        <span
                          key={colabId}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-100 text-indigo-800 text-sm rounded-full"
                        >
                          <UserIcon className="h-4 w-4" />
                          {colab.nome}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Arquivo */}
              {procedimentoVisualizado.arquivo && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">📎 Arquivo Anexado</h3>
                  <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <DocumentArrowDownIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{procedimentoVisualizado.arquivo.nome}</p>
                        <p className="text-sm text-gray-500">
                          {(procedimentoVisualizado.arquivo.tamanho / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleVisualizarArquivo(procedimentoVisualizado)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <EyeIcon className="h-4 w-4" />
                        Visualizar
                      </button>
                      <button
                        onClick={() => handleBaixarArquivo(procedimentoVisualizado)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" />
                        Baixar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Data */}
              <div className="text-sm text-gray-500 border-t pt-4">
                <p>Criado em: {new Date(procedimentoVisualizado.createdAt).toLocaleDateString('pt-BR', { 
                  day: '2-digit', 
                  month: 'long', 
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</p>
              </div>

              {/* Botão Fechar */}
              <div className="mt-6">
                <button
                  onClick={() => {
                    setMostrarModalVisualizacao(false);
                    setProcedimentoVisualizado(null);
                  }}
                  className="w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-sm hover:from-indigo-700 hover:to-purple-700 transition-colors font-medium"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ProcedimentosSimples;
